/**
 * renderInlineSubqueries — splits a buffer-byte-range into alternating
 * text and `<SubquerySection mode='chip'>` nodes. Used by SELECT-list and
 * WHERE/HAVING pills to render scalar / IN-list subqueries in place.
 *
 * For each subquery hit, if it's preceded by an `IN` keyword the chip is
 * rendered with a `prefixLabel='IN'` and the `IN` keyword itself is excluded
 * from the surrounding text run (so the visual reads `x [IN ( SELECT … )]`).
 */
import type { ReactNode } from 'react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { extractSubqueries } from './extractSubqueries';
import { SubquerySection } from './SubquerySection';
import type { SubqueryWiring } from './SelectRenderer';

export interface RenderInlineSubqueriesArgs {
  readonly bufferText: string;
  readonly start: number;
  readonly end: number;
  readonly options: ParseOptions;
  readonly subqueryCtx: SubqueryWiring;
  readonly testIdPrefix: string;
  /** Optional label-prefix used to derive the drill breadcrumb label. */
  readonly drillLabelPrefix: string;
  readonly editContext?: VqeEditContext;
}

export function renderInlineSubqueries({
  bufferText,
  start,
  end,
  options,
  subqueryCtx,
  testIdPrefix,
  drillLabelPrefix,
  editContext,
}: RenderInlineSubqueriesArgs): { nodes: ReactNode[]; subqueryCount: number } {
  const refs = extractSubqueries(bufferText, start, end, options);
  if (refs.length === 0) {
    return {
      nodes: [
        <span key="text-only" className="font-mono text-xs">
          {bufferText.slice(start, end)}
        </span>,
      ],
      subqueryCount: 0,
    };
  }
  const nodes: ReactNode[] = [];
  let cursor = start;
  refs.forEach((ref, idx) => {
    const inlineSplitStart = ref.precededBy === 'IN' && ref.prefixStart !== null
      ? ref.prefixStart
      : ref.parenStart;
    if (inlineSplitStart > cursor) {
      const text = bufferText.slice(cursor, inlineSplitStart);
      if (text.length > 0) {
        nodes.push(
          <span key={`text-${cursor}`} className="font-mono text-xs">
            {text}
          </span>,
        );
      }
    }
    nodes.push(
      <SubquerySection
        key={`sub-${ref.parenStart}`}
        bufferText={bufferText}
        bodyStart={ref.bodyStart}
        bodyEnd={ref.bodyEnd}
        bodyKind={ref.bodyKind}
        options={options}
        parentStatementId={subqueryCtx.statementId}
        drillTabId={subqueryCtx.drillTabId}
        drillLabel={`${drillLabelPrefix} #${idx + 1}`}
        outerAliases={subqueryCtx.outerAliases}
        mode="chip"
        testIdPrefix={`${testIdPrefix}-sub-${idx}`}
        prefixLabel={ref.precededBy === 'IN' ? 'IN' : null}
        onEditAsSql={subqueryCtx.onEditAsSql}
        onDrill={subqueryCtx.onDrill}
        editContext={editContext}
      />,
    );
    cursor = ref.parenEndExclusive;
  });
  if (cursor < end) {
    const tail = bufferText.slice(cursor, end);
    if (tail.length > 0) {
      nodes.push(
        <span key={`text-${cursor}`} className="font-mono text-xs">
          {tail}
        </span>,
      );
    }
  }
  return { nodes, subqueryCount: refs.length };
}
